import Cta from "@components/Cta/Cta";
import Grid from "@components/Grid/Grid";
import Link from "@components/Link/Link";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { RelatedProgrammeDetails } from "@customTypes/programmeDetails";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";

import { NextContentfulImage } from "@components/Image";
import { COVEO_IGNORE_CLASS } from "@utilities/coveoConst";
import { A11y, Keyboard, Pagination } from "swiper/modules";
import { Swiper as SwiperCarousel, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/a11y";
import "swiper/scss/pagination";
import styles from "./C250_RelatedProgrammes.module.scss";

export interface RelatedProgrammesProps {
  title: string;
  image: ImageWrapperType;
  headline: string;
  description: string;
  relatedProgrammes: RelatedProgrammeDetails[];
  BlueVariation?: boolean;
}

export const RelatedProgrammes = (props: {
  content: RelatedProgrammesProps;
}) => {
  const { content } = props;

  const {
    image,
    headline,
    title,
    description,
    relatedProgrammes,
    BlueVariation,
  } = content;

  const device = useDevice();

  const perPage = device === "mobile" ? 1 : device === "tablet" ? 2 : 3;

  return (
    <div
      className={clsx(
        styles["related-courses"],
        image && styles.image,
        COVEO_IGNORE_CLASS
      )}
    >
      <div className="wrapper">
        <Grid row>
          {image && (
            <div className={styles.picture}>
              <NextContentfulImage
                src={image.fields.url}
                alt={image.fields.altText || ""}
                width={800}
                height={800}
              />
            </div>
          )}
          <Grid column sm={12}>
            <p className="label">{headline}</p>
          </Grid>
          <Grid column sm={12} md={6}>
            <h2>{title}</h2>
          </Grid>
          <Grid column sm={12} md={6}>
            <p className={styles.large}>{description}</p>
          </Grid>
        </Grid>
        <div className={styles.programmes}>
          <div className={styles.container}>
            <SwiperCarousel
              className={clsx({
                ["on-dark"]: BlueVariation,
              })}
              spaceBetween={40}
              slidesPerView={perPage}
              loop={!["desktop-large", "desktop"].includes(device)}
              enabled={!["desktop-large", "desktop"].includes(device)}
              pagination={{
                clickable: true,
              }}
              modules={[Pagination, A11y, Keyboard]}
              keyboard={{ enabled: true, onlyInViewport: true }}
              a11y={{ enabled: true }}
            >
              {relatedProgrammes?.map((relatedProgramme, i) => {
                const { slug, programmeTitle, programmeDescription } =
                  relatedProgramme;

                const url = `/${slug}`;

                return (
                  <SwiperSlide key={`${programmeTitle}${i}`}>
                    <div className={styles.programme}>
                      <h3>
                        <Link href={url}>{programmeTitle}</Link>
                      </h3>
                      <p>{programmeDescription}</p>
                      <div className={styles.bottom}>
                        <p>
                          <Cta
                            type="tertiary"
                            url={url}
                            linkText="Read more"
                            icon="icon-arrow"
                          />
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </SwiperCarousel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedProgrammes;
